import { useState } from "react";

import { Link } from "react-router-dom";
import { ClusterType, type ClusterWithCloudConnect } from "@doitintl/cmp-models";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, IconButton, Stack, Typography } from "@mui/material";

import useRouteMatchURL from "../../../../../Components/hooks/useRouteMatchURL";
import { useCustomerContext } from "../../../../../Context/CustomerContext";
import { getAwsCloudConnectSettingsLink } from "../../../../Settings/AWS/utils";
import { getGcpCloudConnectSettingsLink } from "../../../../Settings/GCP/utils";

interface ClusterConnectionIssueAlertProps {
  clusterType: ClusterType;
  clusters: ClusterWithCloudConnect[];
}

const ClusterConnectionIssueAlert = ({ clusterType, clusters }: ClusterConnectionIssueAlertProps) => {
  const routeMatchURL = useRouteMatchURL();
  const { customer } = useCustomerContext();

  const [showAlert, setShowAlert] = useState(true);

  if (!showAlert || clusters.length === 0) {
    return null;
  }

  const getCloudConnectLink = (cluster: ClusterWithCloudConnect) => {
    if (clusterType === ClusterType.GKE) {
      return getGcpCloudConnectSettingsLink({
        customer,
        cloudConnect: cluster.cloudConnect,
        callbackUrl: routeMatchURL,
      });
    } else if (clusterType === ClusterType.EKS) {
      return getAwsCloudConnectSettingsLink({
        customer,
        cloudConnect: cluster.cloudConnect,
        callbackUrl: routeMatchURL,
      });
    }

    return "";
  };

  const projectIdsWithLinks = clusters.map((cluster) => ({
    projectId: cluster.projectId,
    link: getCloudConnectLink(cluster),
  }));

  const message =
    clusterType === ClusterType.GKE ? `Connection issues in GCP projects: ` : `Connection issues in AWS accounts: `;

  return (
    <Alert
      severity="warning"
      action={
        <Stack direction="row">
          <IconButton
            color="inherit"
            size="small"
            onClick={() => {
              setShowAlert(false);
            }}
            sx={{ width: 32 }}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </Stack>
      }
    >
      {message}
      {projectIdsWithLinks.map(({ projectId, link }, index) => (
        <span key={projectId}>
          <Typography
            component={Link}
            to={link}
            color="inherit"
            variant="body2"
            title="Click to enable features"
            sx={{ textDecorationColor: "inherit", cursor: "pointer" }}
          >
            {projectId}
          </Typography>
          {index < projectIdsWithLinks.length - 1 && ", "}
        </span>
      ))}
    </Alert>
  );
};

export default ClusterConnectionIssueAlert;
